// Tiny hyperscript renderer producing real DOM nodes (no virtual DOM, no parsing).
// Usage: h("div", { class: "card", onClick: fn }, "hello", h("span", null, "x"))

const SVG_NS = "http://www.w3.org/2000/svg";
const SVG_TAGS = new Set(["svg", "g", "path", "rect", "circle", "line", "polyline", "polygon", "text", "tspan", "defs", "use"]);

export function h(type, props, ...children) {
  props = props || {};
  if (typeof type === "function") {
    return type({ ...props, children: children.flat(Infinity) });
  }
  const el = SVG_TAGS.has(type) ? document.createElementNS(SVG_NS, type) : document.createElement(type);

  for (const [key, value] of Object.entries(props)) {
    if (value == null || value === false) continue;
    if (key === "class" || key === "className") {
      el.setAttribute("class", Array.isArray(value) ? value.filter(Boolean).join(" ") : value);
    } else if (key === "style" && typeof value === "object") {
      Object.assign(el.style, value);
    } else if (key === "dataset" && typeof value === "object") {
      for (const [d, v] of Object.entries(value)) if (v != null) el.dataset[d] = v;
    } else if (key === "html") {
      el.innerHTML = value;
    } else if (key === "ref" && typeof value === "function") {
      value(el);
    } else if (key.startsWith("on") && typeof value === "function") {
      el.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (key === "value" || key === "checked" || key === "disabled" || key === "selected") {
      el[key] = value; // set as property for form controls
    } else if (el instanceof SVGElement) {
      el.setAttribute(key, value);
    } else {
      el.setAttribute(key, value === true ? "" : value);
    }
  }

  // Accessibility for the tappable-card idiom: any element opting into `card-tap` (the app's
  // "this whole card/row is a button" class) gets button semantics + keyboard activation for
  // free, so the 15+ call sites don't each need role/tabindex/keydown. Keyed on the class (not
  // bare onClick) so incidental click handlers — modal backdrops, toggles — are never affected.
  if (props.onClick && !props.role && el.getAttribute && (el.getAttribute("class") || "").split(/\s+/).includes("card-tap")) {
    el.setAttribute("role", "button");
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0");
    el.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        el.click();
      }
    });
  }

  appendChildren(el, children);
  return el;
}

function appendChildren(el, children) {
  for (const child of children.flat(Infinity)) {
    // Skip falsy non-strings so the `cond && node` idiom never leaks `0`/false/null.
    if (child == null || child === false || child === true || child === 0 || Number.isNaN(child)) continue;
    el.appendChild(child instanceof Node ? child : document.createTextNode(String(child)));
  }
}

/** Replace all children of `parent` with `node`. */
export function render(parent, node) {
  parent.replaceChildren();
  if (node) parent.appendChild(node);
}

/**
 * Replace children, filtering out null/false/0 like h() does.
 * Use instead of el.replaceChildren(...) to avoid `null` -> "null" text nodes.
 */
export function setChildren(el, ...children) {
  el.replaceChildren();
  appendChildren(el, children);
}
